import { fetchCompliance, fetchComplianceVerification } from '@/api/complianceApi';
import PageHeader from '@/components/PageHeader';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { styled } from 'styled-components';

import { ReactComponent as VerifyIcon } from '@/assets/icons/verify.icon.svg';
import { ReactComponent as CopyIcon } from '@/assets/icons/copy.icon.svg';
import { ReactComponent as ExtLinkIcon } from '@/assets/icons/ext-link.icon.svg';
import { ReactComponent as FileIcon } from '@/assets/icons/attachment.icon.svg';
import { ReactComponent as RemarkIcon } from '@/assets/icons/remark.icon.svg';
import { ReactComponent as BackIcon } from '@/assets/icons/back.icon.svg';
import { ReactComponent as MoreIcon } from '@/assets/icons/more.icon.svg';
import { ReactComponent as ArrowUpIcon } from '@/assets/icons/arrow-up.icon.svg';
import { ReactComponent as ArrowDownIcon } from '@/assets/icons/arrow-down.icon.svg';
import { ReactComponent as DropIcon } from '@/assets/icons/nav-arrow-down.icon.svg';
import ApprovalModal from './components/ApprovalModal';
import RejectModal from './components/RejectModal';
import PageLoader from '@/loaders/PageLoader';
import { toast } from 'react-hot-toast';
import withAuthentication from '@/hooks/withAuthentication';
import { usePermissions } from '@/hooks/usePermissions';
import useOnClickOutside from '@/hooks/useClickOutside';
import { AnimatePresence, motion } from 'framer-motion';
import ApproveSelfieModal from './components/ApproveSelfieModal';
import { formatInTimeZone } from 'date-fns-tz';
import VerificationDetailsModal from './components/VerificationDetailsModal';

function ComplianceDetails() {
  const navigate = useNavigate();
  const { search } = useLocation();
  // const queryClient = useQueryClient();
  const { id } = useParams();
  const [showApproveMenu, setShowApproveMenu] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [completedStage, setCompletedStage] = useState(0);
  const [data, setData] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [bvnImage, setBvnImage] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSelfieModal, setShowSelfieModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [verificationDetailsModal, setShowVerificationDetailsModal] = useState(false);

  const { hasPermission, superAdmin } = usePermissions();
  const isASuperAdmin = superAdmin(true);
  const canUpdateCompliance = hasPermission('compliance', 'update');

  const [currentDetailPage, setCurrentDetailPage] = useState(1);

  const params = new URLSearchParams(search);
  const currentPage = params.get('page');

  const ref = useRef();
  const approveRef = useRef();

  useOnClickOutside(ref, () => setShowMenu(false));
  useOnClickOutside(approveRef, () => setShowApproveMenu(false));

  const handleReturn = () => {
    window.history.back();
    // navigate(`/users?page=${currentPage}`);
  };

  useEffect(() => {
    if (currentPage) {
      setCurrentDetailPage(parseInt(currentPage));
    }
  }, [currentPage]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const compliance = useQuery({
    queryKey: ['compliance', { id }],
    queryFn: fetchCompliance,
    onSuccess: (data) => {
      const keysToConsider = [
        'businessType',
        'businessYear',
        'certificateUrl',
        'memorandumUrl',
        'proofOfBusinessUrl',
        'registrationNumber',
        'tin',
        'bvn',
        'aboutBusiness'
      ];

      let nonNullCount = 0;

      keysToConsider.forEach((key) => {
        if (data?.data[key] !== null) {
          nonNullCount++;
        }
      });

      const totalKeys = keysToConsider.length;
      const submissionPercentage = Math.round((nonNullCount / totalKeys) * 100);

      setCompletedStage(submissionPercentage);
    }
  });

  useEffect(() => {
    const verificationJobs = compliance?.data?.data?.user?.userVerificationJobs;
    if (verificationJobs) {
      // Adjust the status condition as needed (e.g., "VERIFIED" or "PENDING")
      const bvnJob = verificationJobs.find(
        (job) => job.type === 'BVN' && job.status === 'VERIFIED'
      );
      // If the job exists and has an image link, store it in state.
      if (bvnJob?.metadata?.image_links?.bvn_image) {
        setBvnImage(bvnJob.metadata.image_links.bvn_image);
      } else {
        setBvnImage(null);
      }
    }
  }, [compliance]);

  const complianceVerification = useQuery({
    queryKey: ['complianceVerification', { id }],
    queryFn: fetchComplianceVerification
  });

  const handleApprove = () => {
    const isAllowed = canUpdateCompliance || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to update');
      return;
    } else {
      setShowApprovalModal(!showApprovalModal);
      console.log(isASuperAdmin);
    }
  };

  const handleSelfieApproval = () => {
    const isAllowed = canUpdateCompliance || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to update');
      return;
    } else {
      setData(compliance?.data?.data);
      setShowSelfieModal(!showSelfieModal);
      console.log(isASuperAdmin);
    }
  };

  const handleReject = () => {
    const isAllowed = canUpdateCompliance || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to update');
      return;
    } else {
      setShowRejectModal(!showRejectModal);
    }
  };

  if (compliance.isFetching || complianceVerification.isFetching) {
    return <PageLoader />;
  }

  return (
    <>
      <PageHeader
        title="Compliance details"
        subtitle="View, approve or decline compliance submissions"
      />
      <ComplianceDetailsView>
        <div className="return" onClick={handleReturn}>
          <BackIcon />
        </div>
        <ComplianceStatus>
          <h3>Verification status</h3>
          <div className="line"></div>
          {compliance?.data?.data?.isVerified ? (
            <div className="status verified">
              <div className="icon">
                <VerifyIcon />
              </div>
              <div className="label-view">
                <span>Approved</span>
              </div>
            </div>
          ) : (
            <div className="status not-verified">
              <div className="icon">
                <VerifyIcon />
              </div>
              <div className="label-view">
                <span>Not verified</span>
              </div>
            </div>
          )}
        </ComplianceStatus>
        <ComplianceProgress>
          <IconView>
            <VerifyIcon />
          </IconView>
          <div className="right-side">
            <div className="progress-title">
              <h3>Submission progress</h3>
              <p>{completedStage}% completed</p>
            </div>
            <ProgressView progress={completedStage}>
              <div className="progress-bar"></div>
            </ProgressView>
          </div>
        </ComplianceProgress>

        <DataView>
          <FormData>
            <div className="item">
              <div className="label-view">
                <h3>User</h3>
                <p>
                  {compliance?.data?.data?.user?.firstName || '-'}{' '}
                  {compliance?.data?.data?.user?.lastName || '-'}
                </p>
              </div>
              <div className="copy-view">
                <button onClick={() => navigate(`/users/${compliance?.data?.data?.user?.id}`)}>
                  View user <ExtLinkIcon />
                </button>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Business name</h3>
                <p>{compliance?.data?.data?.user?.companies[0]?.name}</p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Business type</h3>
                <p>{compliance?.data?.data?.businessType || '-'}</p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Business year</h3>
                <p>{compliance?.data?.data?.businessYear || '-'}</p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Registration number</h3>
                <p>{compliance?.data?.data?.registrationNumber || '-'}</p>
              </div>
              <div className="copy-view">
                <button onClick={() => copyToClipboard(compliance?.data?.data?.registrationNumber)}>
                  Copy <CopyIcon />
                </button>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Tax ID Number (TIN)</h3>
                <p>{compliance?.data?.data?.tin || '-'}</p>
              </div>
              <div className="copy-view">
                <button onClick={() => copyToClipboard(compliance?.data?.data?.tin)}>
                  Copy <CopyIcon />
                </button>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>National Identification Number (NIN)</h3>
                <p>{compliance?.data?.data?.nin || '-'}</p>
              </div>
              <div className="copy-view">
                <button onClick={() => copyToClipboard(compliance?.data?.data?.nin)}>
                  Copy <CopyIcon />
                </button>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Bank Verification Number (BVN)</h3>
                <p>{compliance?.data?.data?.bvn || '-'}</p>
              </div>
              <div className="copy-view">
                <button onClick={() => copyToClipboard(compliance?.data?.data?.bvn)}>
                  Copy <CopyIcon />
                </button>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Website</h3>
                <p>{compliance?.data?.data?.websiteUrl || '-'}</p>
              </div>
              <div className="copy-view">
                {compliance?.data?.data?.websiteUrl && (
                  <a
                    href={compliance?.data?.data?.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer">
                    View <ExtLinkIcon />
                  </a>
                )}
              </div>
            </div>
            <div className="item last">
              <div className="label-view">
                <h3>Business info</h3>
                <p>{compliance?.data?.data?.aboutBusiness || '-'}</p>
              </div>
            </div>
          </FormData>

          <FileView>
            <CTAView>
              <button className="decline" onClick={() => handleReject()}>
                Reject compliance
              </button>
              <button className="select-dropdown" onClick={() => setShowApproveMenu(true)}>
                <p>Select Approval</p>
                <span>
                  {showApproveMenu ? <DropIcon className="up" /> : <DropIcon className="down" />}
                </span>
                {/* Approve compliance{' '} */}
              </button>

              {/* ===== Menu Options ====== */}
              <AnimatePresence>
                {showApproveMenu ? (
                  <motion.div
                    className="menu-dropdown"
                    ref={approveRef}
                    initial={{ y: -20 }}
                    animate={{ y: 0 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.2 }}>
                    <button
                      className="select"
                      onClick={() => {
                        handleApprove();
                      }}>
                      Approve Compliance
                    </button>
                    <button
                      className="select"
                      onClick={() => {
                        handleSelfieApproval();
                      }}>
                      Approve Selfie
                    </button>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </CTAView>

            <h3>Attached files</h3>
            <p>Files and assets that have been attached to this submission</p>

            {compliance?.data?.data?.certificateUrl && (
              <FileItem>
                <div className="icon">
                  <FileIcon />
                </div>
                <div className="label-view">
                  <h3>Business registration certificate</h3>
                  <p>
                    {compliance?.data?.data?.certificateUrl.substring(
                      compliance?.data?.data?.certificateUrl.lastIndexOf('/') + 1
                    )}
                  </p>
                </div>
                <div className="view">
                  <a
                    href={compliance?.data?.data?.certificateUrl}
                    target="_blank"
                    rel="noopener noreferrer">
                    View <ExtLinkIcon />
                  </a>
                </div>
              </FileItem>
            )}

            {compliance?.data?.data?.memorandumUrl && (
              <FileItem>
                <div className="icon">
                  <FileIcon />
                </div>
                <div className="label-view">
                  <h3>Memorandum of Understanding </h3>
                  <p>
                    {compliance?.data?.data?.memorandumUrl.substring(
                      compliance?.data?.data?.memorandumUrl.lastIndexOf('/') + 1
                    )}
                  </p>
                </div>
                <div className="view">
                  <a
                    href={compliance?.data?.data?.memorandumUrl}
                    target="_blank"
                    rel="noopener noreferrer">
                    View <ExtLinkIcon />
                  </a>
                </div>
              </FileItem>
            )}

            {compliance?.data?.data?.proofOfBusinessUrl && (
              <FileItem>
                <div className="icon">
                  <FileIcon />
                </div>
                <div className="label-view">
                  <h3>Utility bill</h3>
                  <p>
                    {compliance?.data?.data?.proofOfBusinessUrl.substring(
                      compliance?.data?.data?.proofOfBusinessUrl.lastIndexOf('/') + 1
                    )}
                  </p>
                </div>
                <div className="view">
                  <a
                    href={compliance?.data?.data?.proofOfBusinessUrl}
                    target="_blank"
                    rel="noopener noreferrer">
                    View <ExtLinkIcon />
                  </a>
                </div>
              </FileItem>
            )}

            <h3 className="verification">Verification report</h3>
            <p className="sub-text">
              Compliance verification report. This report is generated after a compliance has been
              verified
            </p>

            <ComplianceStatus>
              <h3>Compliance status</h3>
              <div className="line"></div>
              {compliance?.data?.data?.verifications[0]?.isValid ? (
                <div className="status verified">
                  <div className="icon">
                    <VerifyIcon />
                  </div>
                  <div className="label-view">
                    <span>Valid</span>
                  </div>
                </div>
              ) : (
                <div className="status not-verified">
                  <div className="icon">
                    <VerifyIcon />
                  </div>
                  <div className="label-view">
                    <span>Invalid</span>
                  </div>
                </div>
              )}
            </ComplianceStatus>

            {compliance?.data?.data?.verifications?.length > 0 && (
              <>
                {compliance?.data?.data?.verifications[0]?.reportDocUrl ? (
                  <FileItem>
                    <div className="icon">
                      <FileIcon />
                    </div>
                    <div className="label-view">
                      <h3>Compliance report</h3>
                      <p>
                        {compliance?.data?.data?.verifications[0]?.reportDocUrl.substring(
                          compliance?.data?.data?.verifications[0]?.reportDocUrl.lastIndexOf('/') +
                            1
                        )}
                      </p>
                    </div>
                    <div className="view">
                      <a
                        href={compliance?.data?.data?.verifications[0]?.reportDocUrl}
                        target="_blank"
                        rel="noopener noreferrer">
                        View <ExtLinkIcon />
                      </a>
                    </div>
                  </FileItem>
                ) : null}

                {compliance?.data?.data?.verifications[0]?.remarks ? (
                  <FileRemark>
                    <div className="icon">
                      <RemarkIcon />
                    </div>
                    <div className="label-view">
                      <h3>Remark</h3>
                      <p>{compliance?.data?.data?.verifications[0]?.remarks}</p>
                    </div>
                  </FileRemark>
                ) : null}
              </>
            )}
          </FileView>
        </DataView>

        <div className="verification-container">
          <h2 className="title">Verification Jobs</h2>
          <div>
            <TableHead>
              <div className="amount">
                <span>Verification Type</span>
              </div>
              <div className="status">
                <span>Status</span>
              </div>
              <div className="created">
                <span>Date Created</span>
              </div>
              <div className="created">
                <span>Date Updated</span>
              </div>
              <div className="actions"></div>
            </TableHead>
            <TableBody>
              {compliance?.data?.data?.user?.userVerificationJobs.map((item, i) => {
                return (
                  <div key={item?.id} className="item">
                    <div className="amount">
                      <span>{item?.type}</span>
                    </div>
                    <Status status={item?.status} className="status">
                      <span className="dot"></span> {item?.status || '-'}
                    </Status>
                    <div className="created">
                      <span>
                        {formatInTimeZone(new Date(item?.createdAt), 'UTC', 'do MMM, yyy')}
                      </span>
                      <p>{formatInTimeZone(new Date(item?.createdAt), 'UTC', 'h:mm aa')}</p>
                    </div>
                    <div className="created">
                      <span>
                        {formatInTimeZone(new Date(item?.updatedAt), 'UTC', 'do MMM, yyy')}
                      </span>
                      <p>{formatInTimeZone(new Date(item?.updatedAt), 'UTC', 'h:mm aa')}</p>
                    </div>
                    <div className="actions">
                      <button
                        className="menu-icon"
                        onClick={() => {
                          setShowMenu(true);
                          setCurrentIndex(i);
                        }}>
                        <MoreIcon />
                      </button>

                      {/* ===== Dropdown Menu ====== */}
                      <AnimatePresence>
                        {showMenu && currentIndex === i ? (
                          <motion.div
                            className="menu-actions"
                            ref={ref}
                            initial={{ y: -20 }}
                            animate={{ y: 0 }}
                            exit={{ y: -10, opacity: 0 }}
                            transition={{ duration: 0.2 }}>
                            <button
                              onClick={() => {
                                setSelectedItem(item);
                                setShowVerificationDetailsModal(true);
                              }}>
                              <span>View details</span>
                            </button>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  </div>
                );
              })}
            </TableBody>
          </div>
        </div>
      </ComplianceDetailsView>

      <ApprovalModal
        showModal={showApprovalModal}
        setShowModal={setShowApprovalModal}
        complianceId={id}
      />

      <ApproveSelfieModal
        showModal={showSelfieModal}
        setShowModal={setShowSelfieModal}
        compliance={data}
      />

      <RejectModal
        showModal={showRejectModal}
        setShowModal={setShowRejectModal}
        complianceId={id}
      />

      <VerificationDetailsModal
        showModal={verificationDetailsModal}
        setShowModal={setShowVerificationDetailsModal}
        selectedItem={selectedItem}
        bvnImage={bvnImage}
        complianceData={compliance?.data?.data}
      />
    </>
  );
}

export default withAuthentication(ComplianceDetails);

const getStatusColor = (status) => {
  switch (status) {
    case 'VERIFIED':
      return '#16A34A';
    case 'PENDING':
      return '#F4B740';
    case 'FAILED':
      return '#B91C1C';

    default:
  }
};

const Status = styled.div`
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 0.75rem;
  text-transform: capitalize;
  color: ${({ status }) => getStatusColor(status)};

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 4px;
    background-color: ${({ status }) => getStatusColor(status)};
  }
`;

const ComplianceDetailsView = styled.div`
  width: 82%;
  padding: 30px;
  background-color: ${({ theme }) => theme.colors.white};
  overflow-y: scroll;
  height: 100%;
  padding-top: 110px;
  overflow-x: hidden;
  margin-left: 18%;

  .return {
    width: 30px;
    margin-bottom: 20px;
    cursor: pointer;
  }

  .progress-title {
    width: 100%;
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    h3 {
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme?.colors?.secondary};
    }

    p {
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme?.colors?.secondary};
    }
  }

  .verification-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 40px;

    .title {
      font-size: 1.4rem;
      font-weight: 600;
      color: ${({ theme }) => theme.colors?.secondary};
    }
  }
`;

const ProgressView = styled.div`
  width: 100%;
  height: 10px;
  background-color: ${({ theme }) => theme.colors?.secondary};
  border-radius: 6px;

  .progress-bar {
    width: ${({ progress }) => `${progress}%`};
    height: 10px;
    background-color: ${({ theme }) => theme.colors?.primary};
    border-radius: 6px;
  }
`;

const ComplianceProgress = styled.div`
  width: 100%;
  margin-bottom: 30px;
  border-radius: 6px;
  padding: 20px;
  background-color: ${({ theme }) => theme.colors?.white};
  border: 1px solid ${({ theme }) => theme.colors?.line};
  display: flex;
  align-items: center;
  justify-content: space-between;

  .right-side {
    width: calc(100% - 70px);
  }
`;

const IconView = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 6px;
  background-color: #f3f2f2;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DataView = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors?.white};
  display: flex;
  justify-content: space-between;
`;

const FormData = styled.div`
  width: 50%;
  background-color: ${({ theme }) => theme.colors?.white};
  border-radius: 6px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors?.line};

  .item {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors?.line};

    .label-view {
      width: 70%;
      display: flex;
      justify-content: center;
      flex-direction: column;

      h3 {
        font-size: 0.875rem;
        font-weight: 500;
        color: ${({ theme }) => theme?.colors?.secondary};
        margin-bottom: 8px;
      }

      p {
        font-size: 0.875rem;
        font-weight: 600;
        color: ${({ theme }) => theme?.colors?.secondary};
      }
    }

    .copy-view {
      width: 120px;
      display: flex;
      align-items: center;

      button,
      a {
        width: 100%;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        color: ${({ theme }) => theme?.colors?.info};
        font-size: 0.875rem;

        svg {
          margin-left: 5px;
          width: 16px;
          height: 16px;

          path {
            stroke: ${({ theme }) => theme?.colors?.info};
          }
        }
      }
    }
  }

  .last {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const FileView = styled.div`
  width: 50%;
  background-color: ${({ theme }) => theme.colors?.white};
  padding-left: 20px;

  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.secondary};
    margin-bottom: 8px;
  }

  .verification {
    margin-top: 40px;
  }

  p {
    font-size: 0.875rem;
    font-weight: 500;
    color: ${({ theme }) => theme?.colors?.secondary};
  }

  .sub-text {
    margin-bottom: 20px;
  }
`;

const FileItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background-color: #f3f2f2;
  border-radius: 6px;
  margin-top: 20px;

  .icon {
    width: 50px;
    height: 50px;
    border-radius: 6px;
    background-color: ${({ theme }) => theme.colors?.white};
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .label-view {
    width: calc(100% - 190px);

    h3 {
      font-size: 0.875rem;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.secondary};
    }

    p {
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme?.colors?.secondary};
    }
  }

  .view {
    width: 120px;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    a {
      color: ${({ theme }) => theme?.colors?.info};
      font-size: 0.875rem;
      display: flex;
      align-items: center;

      svg {
        margin-left: 5px;
        width: 16px;
        height: 16px;

        path {
          stroke: ${({ theme }) => theme?.colors?.info};
        }
      }
    }
  }
`;

const FileRemark = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 20px;
  background-color: #f3f2f2;
  border-radius: 6px;
  margin-top: 20px;

  .icon {
    width: 50px;
    height: 50px;
    border-radius: 6px;
    background-color: ${({ theme }) => theme.colors?.white};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
  }

  .label-view {
    width: calc(100% - 60px);

    h3 {
      font-size: 0.875rem;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.secondary};
    }

    p {
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme?.colors?.secondary};
    }
  }
`;

const CTAView = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 40px;
  position: relative;

  .menu-dropdown {
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 55px;
    right: 0;
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow:
      0px 0px 2px rgba(125, 125, 125, 0.1),
      0px 2px 10px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    z-index: 10;
    max-width: 220px;
    border: none;
    padding: 0px 0px;

    .select {
      width: 100%;
      border-radius: 10px;
      padding: 1.2rem 2rem;
      margin-right: 0;

      &:hover {
        background-color: ${({ theme }) => theme.colors.inputBackground};
      }
    }
  }

  .approve {
    width: 200px;
    height: 50px;
    background-color: ${({ theme }) => theme.colors?.primary};
    color: ${({ theme }) => theme.colors?.white};
    font-size: 0.875rem;
    font-weight: 600;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 20px;
  }

  .select-dropdown {
    width: 200px;
    height: 50px;
    background-color: ${({ theme }) => theme.colors?.white};
    color: ${({ theme }) => theme.colors?.primary};
    font-size: 0.875rem;
    font-weight: 600;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 20px;
    border: 1px solid ${({ theme }) => theme.colors?.secondary};

    .down {
      width: 20px;
      margin-left: 5px;
    }

    .up {
      width: 20px;
      margin-left: 5px;
      transform: rotate(180deg);
    }
  }

  .decline {
    width: 160px;
    height: 50px;
    background-color: transparent;
    color: ${({ theme }) => theme.colors?.error};
    border: 1px solid ${({ theme }) => theme.colors?.error};
    font-size: 0.875rem;
    font-weight: 600;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 20px;
  }

  .disabled-button {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ComplianceStatus = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    font-size: 1rem;
    font-weight: 500;
    color: ${({ theme }) => theme?.colors?.secondary};
  }

  .line {
    width: 1px;
    height: 20px;
    background-color: ${({ theme }) => theme?.colors?.line};
    margin: 0 20px;
  }

  .status {
    width: 150px;
    height: 30px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;

    .icon {
      margin-right: 5px;

      svg {
        width: 16px;
        height: 16px;
      }
    }

    .label-view {
      span {
        font-size: 0.875rem;
        font-weight: 500;
      }
    }
  }

  .not-verified {
    background-color: ${({ theme }) => theme?.colors?.warningBackground};

    .icon {
      svg {
        path {
          stroke: ${({ theme }) => theme?.colors?.warning};
        }
      }
    }

    .label-view {
      span {
        color: ${({ theme }) => theme?.colors?.warning};
      }
    }
  }

  .verified {
    background-color: ${({ theme }) => theme?.colors?.successBackground};

    .icon {
      svg {
        path {
          stroke: ${({ theme }) => theme?.colors?.success};
        }
      }
    }

    .label-view {
      span {
        color: ${({ theme }) => theme?.colors?.success};
      }
    }
  }
`;

const TableHead = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};

  div {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    cursor: pointer;
    height: 100%;

    span {
      font-size: 0.8rem;
      font-weight: 500;
      color: ${({ theme }) => theme.colors?.black};
      /* text-transform: uppercase; */
    }

    .icon {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin-left: 4px;
      width: 10px;
    }
  }

  .ascending {
    color: ${({ theme }) => theme.colors.black};

    .up path {
      stroke: ${({ theme }) => theme.colors.black};
      color: ${({ theme }) => theme.colors.black};
    }
  }

  .descending {
    color: ${({ theme }) => theme.colors.black};

    .down path {
      stroke: ${({ theme }) => theme.colors.black};
      color: ${({ theme }) => theme.colors.black};
    }
  }

  .channel {
    flex: 0.7;
  }

  .event {
    flex: 0.7;
  }

  .provider {
    flex: 0.3;
  }

  .amount {
    flex: 0.7;
  }

  .remark {
    flex: 1;
  }

  .status {
    flex: 0.7;
  }

  .created {
    flex: 0.5;
  }

  .updated {
    flex: 0.5;
  }

  .actions {
    flex: 0.2;
  }
`;

const TableBody = styled.div`
  width: 100%;

  .item {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-bottom: 1px solid ${({ theme }) => theme.colors?.line};

    div {
      flex: 1;
      flex-wrap: wrap;

      span {
        font-size: 0.75rem;
        font-weight: 500;
        color: ${({ theme }) => theme.colors?.secondary};
        text-transform: capitalize;
      }

      p {
        font-size: 0.75rem;
        font-weight: 500;
        color: ${({ theme }) => theme.colors?.secondary};
        text-transform: capitalize;
      }
    }

    .channel {
      flex: 0.7;
    }

    .event {
      flex: 0.7;
    }

    .provider {
      flex: 0.3;
    }

    .amount {
      flex: 0.7;
    }

    .remark {
      flex: 1;
    }

    .status {
      flex: 0.7;
    }

    .created {
      flex: 0.5;
    }

    .updated {
      flex: 0.5;
    }

    .actions {
      flex: 0.2;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      position: relative;

      svg {
        cursor: pointer;

        path {
          stroke: ${({ theme }) => theme.colors?.secondary};
        }
      }

      .menu-actions {
        position: absolute;
        top: 22px;
        right: 0px;
        background-color: ${({ theme }) => theme.colors.white};
        box-shadow:
          0px 0px 2px rgba(125, 125, 125, 0.1),
          0px 2px 10px rgba(0, 0, 0, 0.1);
        border-radius: 10px;
        z-index: 10;
        width: 180px;
        border: none;

        button {
          width: 100%;
          padding: 16px 12px;
          font-size: 0.875rem;
          background-color: transparent;
          transition: all 0.2s ease-in-out;
          border: none;
          outline: none;
          display: flex;
          justify-content: flex-start;
          align-items: center;

          &:hover {
            background-color: ${({ theme }) => theme.colors.layer3};
          }

          span {
            color: ${({ theme }) => theme.colors.activeTitle};
            width: calc(100% - 35px);
            display: flex;
            justify-content: flex-start;
            text-align: left;
            font-weight: 400;
          }
        }
      }
    }
  }
`;
